/**
 * After a deploy, a tab still running the old build asks for code-split
 * chunks whose hashed names no longer exist on the server. Opening any lazy
 * screen then fails to load its module. Instead of the crash screen, reload
 * once so the tab picks up the new build.
 */

const KEY = 'popin:stale-build-reload';
/** Never reload again within this window: a real outage must not loop. */
const WINDOW_MS = 30_000;

/** Errors browsers throw when a dynamic import or its preload can't be fetched. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading (CSS )?chunk \S+ failed/i.test(
    msg,
  );
}

/**
 * Reloads the page to fetch the current build. Returns false (and does
 * nothing) if it already reloaded for this reason in the last 30 s.
 */
export function reloadForNewBuild(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (now - last < WINDOW_MS) return false;
    sessionStorage.setItem(KEY, String(now));
  } catch {
    // Storage blocked: we couldn't guard against a loop, so leave it to the
    // crash screen's Reload button.
    return false;
  }
  window.location.reload();
  return true;
}

/** Vite's preload helper reports failed dynamic imports on window. */
export function installStaleBuildReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}
